import {
  adminUpTransVars,
  defaultUpTransVars,
  deleteStepUpTransformers,
  ecopetrolUpTransVars,
  kocUpTransVars,
  restoreStepUpTransformers
} from "@/models/equipment/step_up_transformer/predicates";
import type { Step_Up_Transformers } from "gql/api";
import {
  useStepUpTransformersQuery,
  useInsertUpTransMutation,
  useUpdateUpTransByPkMutation,
  useDeleteUpTransMutation,
  useRestoreUpTransMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { sutTableColumns } from "./sutTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useStepUpTransformers(
  isAdmin: boolean
): UseEquipment<Step_Up_Transformers> {
  const baseEquipment = useEquipment<Step_Up_Transformers>(isAdmin);
  const title = "Step Up Transformers";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocUpTransVars;
    if (user.is_ecopetrol_customer) return ecopetrolUpTransVars;
    if (user.is_admin) return adminUpTransVars;
    return defaultUpTransVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useStepUpTransformersQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () => data.value?.step_up_transformers as Step_Up_Transformers[] | undefined
  );

  const hashOrder: (keyof Step_Up_Transformers)[] = [
    "manufacturer",
    "part_number",
    "transformer_type",
    "primary_voltage",
    "min_secondary_voltage",
    "max_secondary_voltage",
    "enclosure",
    "kva"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertUpTrans } = useInsertUpTransMutation();
  const { executeMutation: executeUpdateUpTrans } =
    useUpdateUpTransByPkMutation();
  const { executeMutation: executeDeleteUpTrans } = useDeleteUpTransMutation();
  const { executeMutation: executeRestoreUpTrans } =
    useRestoreUpTransMutation();

  const insertUpTrans = (
    uptrans: Omit<Step_Up_Transformers, "sizing_import" | "user">
  ) => executeInsertUpTrans({ object: uptrans });

  const updateUpTrans = (id: string, uptrans: Step_Up_Transformers) =>
    executeUpdateUpTrans({ id, object: uptrans });

  const deleteUpTrans = (ids: string[]) =>
    executeDeleteUpTrans({ where: deleteStepUpTransformers(ids) });

  const restoreUpTrans = (ids: string[]) =>
    executeRestoreUpTrans({ where: restoreStepUpTransformers(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "step_up_transformers",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    transformer_type: "",
    enclosure: "",
    primary_voltage: null,
    min_secondary_voltage: null,
    max_secondary_voltage: null,
    weight_pounds: null,
    kva: null
  }));

  return {
    searchPlaceholder: "Search Step Up Transformers...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "300"`,
    columns: defineVisibleColumns(sutTableColumns),
    handleUpsert: defineUpsert(insertUpTrans, updateUpTrans),
    handleDelete: defineDelete(deleteUpTrans),
    handleRestore: defineRestore(restoreUpTrans),
    totalCount: computed(
      () => data.value?.step_up_transformers_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: sutTableColumns,
      globalSearchFields: ["kva", "transformer_type", "enclosure"],
      allRows,
      title
    })
  };
}
